import type { WithId } from '@medplum/core';
import type { Dosage, MedicationRequest, MedicationStatement, Quantity } from '@medplum/fhirtypes';
import { MEDICATION_CODE_SYSTEMS, VENDOR_MEDICATION_IDENTIFIER_SYSTEMS } from './identifiers';

export type MedicationListRecord = WithId<MedicationRequest | MedicationStatement>;

/** The primary dosage off a medication record, regardless of resource type: `MedicationRequest`
 * carries it on `dosageInstruction[]`, `MedicationStatement` on `dosage[]`. Lets dosage-reading
 * consumers stay agnostic to which resource backs a row. */
export function getMedicationDosage(record: MedicationListRecord | undefined): Dosage | undefined {
  if (!record) return undefined;
  return record.resourceType === 'MedicationRequest' ? record.dosageInstruction?.[0] : record.dosage?.[0];
}

/** How the e-prescriber's webhook rewrites `note` when a prescription is signed:
 * `Internal comment: <whatever it found there> [medplum-mr:<order id>]`. */
const VENDOR_NOTE_WRAPPER = /^Internal comment:\s*(.*?)\s*\[medplum-mr:[^\]]*\]$/s;

/**
 * The clinician-authored text inside a medication `note`, with the vendor's framing stripped.
 *
 * `undefined` for a blank note and for the wrapper around nothing — a prescription written directly
 * in ScriptSure, which never carried a Liza instruction. Only `note[0]` is ever the clinician's;
 * later entries are the vendor's own (#10277).
 */
export function clinicianNoteText(noteText: string | undefined): string | undefined {
  const text = noteText?.trim();
  if (!text) return undefined;
  const unwrapped = VENDOR_NOTE_WRAPPER.exec(text)?.[1]?.trim();
  return unwrapped === undefined ? text : unwrapped || undefined;
}

/** The outbound copy of the additional instructions on the record itself — what was sent to the
 * e-prescriber, present until signing folds it into the sig and drops the field.
 *
 * For anything a patient sees, read `getTaskInstructions` off the care-plan Task instead: that copy
 * is Liza's and survives prescribing. This one is for the two admin surfaces that have a medication
 * and no Task (#10277). Deliberately never falls back to `note`, which is the vendor's. */
export function getMedicationPatientInstruction(record: MedicationListRecord | undefined): string | undefined {
  return getMedicationDosage(record)?.patientInstruction?.trim() || undefined;
}

function parseInstant(value: string | undefined): number {
  if (!value) {
    return 0;
  }
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? 0 : ms;
}

export function getMedicationRecordSortTimestamp(record: MedicationListRecord): number {
  if (record.resourceType === 'MedicationRequest') {
    return parseInstant(record.authoredOn ?? record.meta?.lastUpdated);
  }

  return parseInstant(
    record.effectiveDateTime ??
      record.effectivePeriod?.end ??
      record.effectivePeriod?.start ??
      record.dateAsserted ??
      record.meta?.lastUpdated,
  );
}

/** The RxNorm code on a medication record's `medicationCodeableConcept`, or `undefined` if it carries
 * no RxNorm coding (e.g. a vendor order coded NDC-only, or an uncoded free-text med). The code value is
 * the stable join key for grouping — RxNorm is the only code system that survives the vendor round-trip
 * unchanged (NDC varies even within one order's lifecycle). Ignores `display`/`tty`. */
export function getMedicationRxNormCode(record: MedicationListRecord): string | undefined {
  // `medicationCodeableConcept` is a common field on both MedicationRequest and MedicationStatement.
  return record.medicationCodeableConcept?.coding?.find((c) => c.system === MEDICATION_CODE_SYSTEMS.RXNORM)?.code;
}

/** The dispense quantity persisted on a medication record (`dispenseRequest.quantity` — value is
 * the amount, `unit` the NCI potency-unit code, matching the eRx order-draft convention), or
 * `undefined` for a `MedicationStatement` (which has no dispense concept) or a record carrying
 * none. On a Liza plan this is the picked formulation's saved dispense detail (#6955). */
export function getMedicationDispenseQuantity(record: MedicationListRecord | undefined): Quantity | undefined {
  if (record?.resourceType !== 'MedicationRequest') return undefined;
  return record.dispenseRequest?.quantity;
}

/** Whether a record is a prescription written back by ScriptSure, as opposed to a Liza-authored
 * self-reported med. A synced-back prescription is a `MedicationRequest` with `intent: 'order'`
 * carrying a ScriptSure prescription identifier. Liza only ever authors `intent: 'plan'`, so the
 * vendor prescription id is the reliable discriminator. */
export function isVendorAuthoredOrder(record: MedicationListRecord): boolean {
  if (record.resourceType !== 'MedicationRequest' || record.intent !== 'order') return false;
  return (record.identifier ?? []).some(
    (id) => id.system === VENDOR_MEDICATION_IDENTIFIER_SYSTEMS.SCRIPTSURE_PRESCRIPTION,
  );
}

/** One displayed medication: the record shown in the list plus any records it supersedes/absorbs.
 * `record` is the authoritative one to render; `superseded` are the others in the group (e.g. the
 * self-reported `plan` collapsed behind its prescription) — kept so callers can still resolve a Task
 * focus that points at a superseded id, or surface provenance later. */
export type MedicationGroup = {
  record: MedicationListRecord;
  superseded: MedicationListRecord[];
};

/** Newest-first comparator for medication records, with `id` as a deterministic tie-break so that
 * equal timestamps (e.g. a plan and order authored the same day, or both undated → 0) don't make the
 * displayed record depend on upstream fetch order. */
function byNewestThenId(a: MedicationListRecord, b: MedicationListRecord): number {
  const delta = getMedicationRecordSortTimestamp(b) - getMedicationRecordSortTimestamp(a);
  return delta !== 0 ? delta : a.id.localeCompare(b.id);
}

/** Pick the authoritative record from a non-empty set sharing one RxNorm code: a vendor-authored
 * prescription wins (newest, if several from repeated re-ingestion), else the newest record. */
function collapseGroup(group: [MedicationListRecord, ...MedicationListRecord[]]): MedicationGroup {
  const sorted = [...group].sort(byNewestThenId);
  // sorted is non-empty: sort preserves length and `group` is a non-empty tuple.
  const newest = sorted[0]!;
  const record = sorted.find(isVendorAuthoredOrder) ?? newest;
  return { record, superseded: sorted.filter((m) => m !== record) };
}

/** Group records that represent the same medication into one displayable row, newest group first.
 *
 * The match key is the RxNorm code (see {@link getMedicationRxNormCode}): a Liza self-reported `plan`
 * and an e-prescriber `order` for the same drug are separate, unlinked resources, so without grouping
 * the same medication shows as two rows. We collapse them to one. The vendor `order` is authoritative
 * (it carries status + dispense detail), so when a group contains one, it becomes the displayed record
 * and the rest are superseded; otherwise the newest record is displayed.
 *
 * Records with no RxNorm code can't be safely matched (an NDC-only order needs an RxNorm↔NDC crosswalk,
 * not yet built), so each is its own group — never merged on weaker signals. */
export function groupMedicationRecords(records: MedicationListRecord[]): MedicationGroup[] {
  const byRxNorm = new Map<string, [MedicationListRecord, ...MedicationListRecord[]]>();
  const ungrouped: MedicationListRecord[] = [];

  for (const record of records) {
    const code = getMedicationRxNormCode(record);
    if (code === undefined) {
      ungrouped.push(record);
      continue;
    }
    const existing = byRxNorm.get(code);
    if (existing) {
      existing.push(record);
    } else {
      byRxNorm.set(code, [record]);
    }
  }

  const groups: MedicationGroup[] = [...byRxNorm.values()].map(collapseGroup);
  for (const record of ungrouped) {
    groups.push({ record, superseded: [] });
  }

  return groups.sort((a, b) => byNewestThenId(a.record, b.record));
}

/** Every record in a group, newest first — the displayed one together with everything it supersedes.
 * For history views, which show the whole group in date order rather than the single authoritative
 * record; `MedicationGroup` itself keeps the two apart because display needs them apart. */
export function medicationGroupHistory(group: MedicationGroup): MedicationListRecord[] {
  return [group.record, ...group.superseded].sort(byNewestThenId);
}

/** The displayable medication records: one authoritative record per {@link groupMedicationRecords}
 * group, newest first. Drop-in replacement for a flat merged list in list views — collapses a
 * self-reported `plan` and its synced-back prescription `order` into the single row that should
 * show. (Superseded records are dropped from display; use `groupMedicationRecords` if you need them.) */
export function groupMedicationRecordsForDisplay(records: MedicationListRecord[]): MedicationListRecord[] {
  return groupMedicationRecords(records).map((group) => group.record);
}

/** Index medications by id, for resolving a Task's `focus` reference to its backing resource.
 * A medication Task's focus is either a `MedicationRequest` (Liza-authored) or a legacy/ingested
 * `MedicationStatement`; callers fetch both lists and pass them here to get one lookup map. Ids are
 * globally-unique per resource and a focus names exactly one type, so the two sets never collide. */
export function indexMedicationsById(
  requests: WithId<MedicationRequest>[],
  statements: WithId<MedicationStatement>[],
): Map<string, MedicationListRecord> {
  const byId = new Map<string, MedicationListRecord>();
  for (const record of [...requests, ...statements]) {
    byId.set(record.id, record);
  }
  return byId;
}

/**
 * The status label key for a medication row, for `t()` at the call site.
 *
 * Two combinations get a word the clinician actually uses, because they are the two states most
 * medications sit in and "active" says nothing about which:
 *
 * - `plan` + `active` → **reported** — the patient says they take this; nothing was sent anywhere.
 * - `order` + `active` → **prescribed** — a signed prescription, live at the pharmacy.
 *
 * Everything else falls through to the raw FHIR status. That is deliberate for now: it keeps every
 * state visible without inventing vocabulary for the ones whose clinical weight has not been settled
 * (#8029). A status we have never seen renders as itself rather than being mislabelled.
 *
 * `MedicationStatement` (legacy/ingested records) has no `intent`, so it can only ever produce a raw
 * status.
 */
export function medicationStatusLabelKey(record: MedicationListRecord | undefined): string | undefined {
  if (!record?.status) return undefined;
  if (record.resourceType === 'MedicationRequest' && record.status === 'active') {
    if (record.intent === 'plan') return 'reported';
    if (record.intent === 'order') return 'prescribed';
  }
  return record.status;
}

/**
 * Whether this status needs the clinician's attention rather than merely describing the record.
 *
 * Only a transmission error qualifies today: the prescription looks sent but never reached the
 * pharmacy, and nothing else in the UI says so (#7971). The other non-active statuses are ordinary
 * history — a stopped or cancelled medication is information, not a call to act.
 */
export function isMedicationStatusUrgent(record: MedicationListRecord | undefined): boolean {
  return record?.resourceType === 'MedicationRequest' && record.status === 'on-hold';
}

/** The vendor's explanation for the current status, when it gave one — the fuller `text` in
 * preference to the coded `display`. Present in practice on a transmission error, where the reason
 * is the actionable part and the status alone reads as innocuous. Vendor-authored, so not
 * translatable. */
export function medicationStatusReasonText(record: MedicationListRecord | undefined): string | undefined {
  if (record?.resourceType !== 'MedicationRequest') return undefined;
  const reason = record.statusReason;
  return reason?.text ?? reason?.coding?.[0]?.display;
}
